import type { JobPosting, Place, WithContext } from "schema-dts";
import countriesData from "./countries.json";

interface Timezone {
    zoneName: string;
    gmtOffset: number;
    gmtOffsetName: string;
    abbreviation: string;
    tzName: string;
}

interface Translations {
    kr: string;
    br: string;
    pt: string;
    nl: string;
    hr: string;
    fa: string;
    de: string;
    es: string;
    fr: string;
    ja: string;
    it: string;
    cn: string;
}

interface Country {
    id: number;
    name: string;
    iso3: string;
    iso2: string;
    numeric_code: string;
    phone_code: string;
    capital: string;
    currency: string;
    currency_name: string;
    currency_symbol: string;
    tld: string;
    native: string;
    region: string;
    subregion: string;
    timezones: Timezone[];
    translations: Translations;
    latitude: string;
    longitude: string;
    emoji: string;
    emojiU: string;
}

const countries: Country[] = countriesData as Country[];

function getCountryByCompleoId(compleoId: number) {
    return countries.find((c) => c.id === compleoId);
}

function getCountryIso2Code(compleoId: number) {
    const country = getCountryByCompleoId(compleoId);
    return country ? country.iso2 : undefined;
}

export function getJsonLd(jobData: any, language: string, company: any) {
    const countryCode =
        jobData.location?.country?.value === "31" || !jobData.location?.country
            ? "BR"
            : getCountryIso2Code(Number(jobData.location.country.value));

    const jsonLd: WithContext<JobPosting> = {
        "@context": "https://schema.org",
        "@type": "JobPosting",
        title:
            language === "pt-BR"
                ? jobData.title
                : jobData.translatedData[language]
                ? jobData.translatedData[language]["title"]
                : jobData.title,
        description:
            language === "pt-BR"
                ? jobData.description
                : jobData.translatedData[language]
                ? jobData.translatedData[language]["description"]
                : jobData.description,
        hiringOrganization: {
            "@type": "Organization",
            name: company?.companyName ?? company?.companyFancyName,
        },
        datePosted: jobData.openingDate ?? jobData.createdAt,
        jobLocation: {
            "@type": "Place",
            address: {
                "@type": "PostalAddress",
                addressCountry: countryCode,
                addressLocality: jobData.location?.city?.label,
                addressRegion:
                    jobData.location?.provinceOrState?.value ??
                    jobData.location?.city?.uf,
            },
        },
    };

    if (jobData?.workingModel?.value === "CP1") {
        //Remoto
        jsonLd["jobLocationType"] = "TELECOMMUTE";

        const country = getCountryByCompleoId(
            jobData.location?.country?.value
                ? Number(jobData.location.country.value)
                : 31
        );
        jsonLd["applicantLocationRequirements"] = {
            "@type": "Country",
            name: country?.name ?? "Brazil",
        };
    }

    if (jobData?.hiringEndDate) {
        jsonLd["validThrough"] = jobData.hiringEndDate;
    }

    if (jobData?.employmentType?.value) {
        switch (jobData.employmentType.value) {
            case "CP1":
                jsonLd["employmentType"] = "FULL_TIME";
                break;
            case "CP2":
                jsonLd["employmentType"] = "INTERN";
                break;
            case "CP3":
                jsonLd["employmentType"] = "CONTRACTOR";
                break;
            default:
                break;
        }
    }

    return jsonLd;
}
